"use client";

import { useRef, useEffect, useState, useCallback } from "react";
import {
  Trash2,
  Undo,
  RotateCcw,
  Pentagon,
  Spline,
  Plus,
  Magnet,
  MousePointerClick,
  Move,
  Keyboard,
  Layers,
  CheckCircle2,
} from "lucide-react";

export interface Point {
  x: number;
  y: number;
}

export interface Polygon {
  id: string;
  points: Point[];
}

export type ShapeMode = "rounded" | "straight";

/** Serializable polygon data using normalized (0-1) coordinates */
export interface PolygonData {
  polygons: { id: string; points: { x: number; y: number }[] }[];
  shapeMode: ShapeMode;
}

interface AreaSelectorProps {
  imageUrl: string;
  onMaskGenerated: (mask: string) => void;
  initialMask?: string | null;
  /** Callback with serializable polygon coordinate data (normalized 0-1) */
  onPolygonDataChange?: (data: PolygonData | null) => void;
  /** Restore polygons from previously saved data (normalized 0-1) */
  initialPolygonData?: PolygonData | null;
  /** Compact mode: no title/subtitle/instructions, floating toolbar overlay */
  compact?: boolean;
  /**
   * Replace the drawn polygons from outside (e.g. "Fix it for me" growing a
   * too-small outline). Applied each time `version` changes; stays editable.
   */
  polygonOverride?: { data: PolygonData; version: number } | null;
}

// Distance threshold (in canvas pixels) to snap to the first point and close the polygon
const CLOSE_THRESHOLD = 15;
// Distance from canvas edge to trigger snap-to-edge
const EDGE_SNAP_THRESHOLD = 30;
// Distance to grab a control point for dragging
const POINT_GRAB_THRESHOLD = 12;
// Catmull-Rom tension (0 = sharp, 1 = very smooth)
const TENSION = 0.5;
// How many line segments per spline segment (smoothness resolution)
const SPLINE_SEGMENTS = 20;

// Colors
const UI_FILL = "rgba(34, 197, 94, 0.30)";
const UI_STROKE = "rgba(34, 197, 94, 0.85)";
const UI_POINT = "rgba(34, 197, 94, 1)";
const UI_SNAP = "rgba(255, 255, 255, 0.9)";
const UI_EDGE_SNAP = "rgba(59, 130, 246, 0.5)"; // blue for edge snap indicator
const UI_DRAG_POINT = "rgba(59, 130, 246, 1)"; // blue for dragged point
const MASK_FILL = "rgb(239, 68, 68)";

// ─── Catmull-Rom Spline Math ───────────────────────────────────────────────

/**
 * Compute a single Catmull-Rom interpolation between p1 and p2,
 * using p0 and p3 as tangent-influencing neighbors.
 * t ∈ [0, 1], alpha = tension
 */
function catmullRomPoint(
  p0: Point,
  p1: Point,
  p2: Point,
  p3: Point,
  t: number,
  alpha: number
): Point {
  const t2 = t * t;
  const t3 = t2 * t;

  // Catmull-Rom basis matrix with tension
  const m0x =
    -alpha * p0.x + (2 - alpha) * p1.x + (alpha - 2) * p2.x + alpha * p3.x;
  const m1x =
    2 * alpha * p0.x +
    (alpha - 3) * p1.x +
    (3 - 2 * alpha) * p2.x -
    alpha * p3.x;
  const m2x = -alpha * p0.x + alpha * p2.x;
  const m3x = p1.x;

  const m0y =
    -alpha * p0.y + (2 - alpha) * p1.y + (alpha - 2) * p2.y + alpha * p3.y;
  const m1y =
    2 * alpha * p0.y +
    (alpha - 3) * p1.y +
    (3 - 2 * alpha) * p2.y -
    alpha * p3.y;
  const m2y = -alpha * p0.y + alpha * p2.y;
  const m3y = p1.y;

  return {
    x: m0x * t3 + m1x * t2 + m2x * t + m3x,
    y: m0y * t3 + m1y * t2 + m2y * t + m3y,
  };
}

/**
 * Generate a smooth closed curve through the given control points using
 * Catmull-Rom splines. Returns an array of densely sampled points.
 */
function catmullRomClosedCurve(
  controlPoints: Point[],
  segments: number = SPLINE_SEGMENTS,
  tension: number = TENSION
): Point[] {
  const n = controlPoints.length;
  if (n < 3) return controlPoints;

  const result: Point[] = [];

  for (let i = 0; i < n; i++) {
    const p0 = controlPoints[(i - 1 + n) % n];
    const p1 = controlPoints[i];
    const p2 = controlPoints[(i + 1) % n];
    const p3 = controlPoints[(i + 2) % n];

    for (let s = 0; s < segments; s++) {
      const t = s / segments;
      result.push(catmullRomPoint(p0, p1, p2, p3, t, tension));
    }
  }

  // Close the loop
  if (result.length > 0) {
    result.push({ ...result[0] });
  }

  return result;
}

// ─── Edge Snap Logic ───────────────────────────────────────────────────────

/**
 * For each control point near a canvas edge, snap it to the edge.
 * Then for the mask, if two consecutive snapped-to-edge points are on the same
 * edge, they form a boundary segment. If they're on adjacent edges, insert the
 * corner point between them.
 */
function snapPointsToEdges(
  points: Point[],
  canvasW: number,
  canvasH: number,
  enabled: boolean,
  threshold: number = EDGE_SNAP_THRESHOLD
): Point[] {
  if (!enabled) return points;
  return points.map((p) => {
    const snapped = { ...p };
    if (p.x <= threshold) snapped.x = 0;
    if (p.x >= canvasW - threshold) snapped.x = canvasW;
    if (p.y <= threshold) snapped.y = 0;
    if (p.y >= canvasH - threshold) snapped.y = canvasH;
    return snapped;
  });
}

/**
 * Determine which edge a point lies on (after snapping).
 * Returns null if the point is interior.
 */
type Edge = "top" | "bottom" | "left" | "right" | null;
function getEdge(p: Point, canvasW: number, canvasH: number): Edge {
  if (p.y <= 0) return "top";
  if (p.y >= canvasH) return "bottom";
  if (p.x <= 0) return "left";
  if (p.x >= canvasW) return "right";
  return null;
}

/**
 * Get the corner point where two edges meet.
 */
function getCorner(
  edge1: Edge,
  edge2: Edge,
  canvasW: number,
  canvasH: number
): Point | null {
  const edgeSet = new Set([edge1, edge2]);
  if (edgeSet.has("top") && edgeSet.has("left")) return { x: 0, y: 0 };
  if (edgeSet.has("top") && edgeSet.has("right")) return { x: canvasW, y: 0 };
  if (edgeSet.has("bottom") && edgeSet.has("left")) return { x: 0, y: canvasH };
  if (edgeSet.has("bottom") && edgeSet.has("right"))
    return { x: canvasW, y: canvasH };
  return null;
}

/**
 * Compute the signed area of a polygon using the shoelace formula.
 * Positive = clockwise winding (in canvas coords where Y increases downward).
 * Negative = counter-clockwise winding.
 */
function signedArea(points: Point[]): number {
  let area = 0;
  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length;
    area += points[i].x * points[j].y;
    area -= points[j].x * points[i].y;
  }
  return area / 2;
}

/**
 * Get ALL corners needed to traverse from edge1 to edge2 along the canvas boundary.
 * Direction follows the polygon's winding: clockwise polygons get clockwise corners,
 * counter-clockwise polygons get counter-clockwise corners. This ensures the
 * boundary expansion fills the inside of the shape, not the outside.
 */
function getCornersBetweenEdges(
  edge1: Edge,
  edge2: Edge,
  canvasW: number,
  canvasH: number,
  clockwise: boolean = true
): Point[] {
  if (!edge1 || !edge2 || edge1 === edge2) return [];

  const edgeOrder: Edge[] = ["top", "right", "bottom", "left"];
  const corners = [
    { x: canvasW, y: 0 }, // top → right
    { x: canvasW, y: canvasH }, // right → bottom
    { x: 0, y: canvasH }, // bottom → left
    { x: 0, y: 0 }, // left → top
  ];

  const idx1 = edgeOrder.indexOf(edge1);
  const idx2 = edgeOrder.indexOf(edge2);

  const result: Point[] = [];
  let current = idx1;
  if (clockwise) {
    // Go clockwise: top → right → bottom → left
    while (current !== idx2) {
      result.push(corners[current]);
      current = (current + 1) % 4;
    }
  } else {
    // Go counter-clockwise: top → left → bottom → right
    // CCW corners: each index i gives the corner going from edge i to the previous edge
    const ccwCorners = [
      { x: 0, y: 0 }, // top → left
      { x: canvasW, y: 0 }, // right → top
      { x: canvasW, y: canvasH }, // bottom → right
      { x: 0, y: canvasH }, // left → bottom
    ];
    while (current !== idx2) {
      result.push(ccwCorners[current]);
      current = (current - 1 + 4) % 4;
    }
  }

  return result;
}

/**
 * Expand a polygon so that edge-snapped segments hug the canvas boundary.
 * Inserts boundary corner points between consecutive edge-snapped points
 * that lie on different edges.
 */
function expandToEdges(
  points: Point[],
  canvasW: number,
  canvasH: number,
  snapEnabled: boolean
): Point[] {
  const snapped = snapPointsToEdges(points, canvasW, canvasH, snapEnabled);
  if (!snapEnabled) return snapped;
  // Detect winding direction of the snapped polygon to choose correct corner traversal
  const isClockwise = signedArea(snapped) > 0;
  const result: Point[] = [];

  for (let i = 0; i < snapped.length; i++) {
    const current = snapped[i];
    const next = snapped[(i + 1) % snapped.length];

    result.push(current);

    const currentEdge = getEdge(current, canvasW, canvasH);
    const nextEdge = getEdge(next, canvasW, canvasH);

    // If both points are on edges but different ones, insert corner(s)
    // Direction matches polygon winding so we fill the inside, not outside
    if (currentEdge && nextEdge && currentEdge !== nextEdge) {
      const corners = getCornersBetweenEdges(
        currentEdge,
        nextEdge,
        canvasW,
        canvasH,
        isClockwise
      );
      result.push(...corners);
    }
  }

  return result;
}

/**
 * Check if a point is near a canvas edge.
 */
function isNearEdge(
  p: Point,
  canvasW: number,
  canvasH: number,
  threshold: number = EDGE_SNAP_THRESHOLD
): boolean {
  return (
    p.x <= threshold ||
    p.x >= canvasW - threshold ||
    p.y <= threshold ||
    p.y >= canvasH - threshold
  );
}

// ─── Path Drawing Helpers ──────────────────────────────────────────────────

/**
 * Trace a smooth closed path on a canvas context from control points.
 */
function traceSmoothedPath(
  ctx: CanvasRenderingContext2D,
  controlPoints: Point[],
  canvasW: number,
  canvasH: number,
  snapEnabled: boolean
) {
  // First expand points to edges, then smooth the expanded shape
  const expanded = expandToEdges(controlPoints, canvasW, canvasH, snapEnabled);
  const smoothed = catmullRomClosedCurve(expanded);

  if (smoothed.length < 2) return;

  ctx.beginPath();
  ctx.moveTo(smoothed[0].x, smoothed[0].y);
  for (let i = 1; i < smoothed.length; i++) {
    ctx.lineTo(smoothed[i].x, smoothed[i].y);
  }
  ctx.closePath();
}

/**
 * Trace a straight-line closed path with edge snapping.
 */
function traceStraightPath(
  ctx: CanvasRenderingContext2D,
  controlPoints: Point[],
  canvasW: number,
  canvasH: number,
  snapEnabled: boolean
) {
  const expanded = expandToEdges(controlPoints, canvasW, canvasH, snapEnabled);

  if (expanded.length < 3) return;

  ctx.beginPath();
  ctx.moveTo(expanded[0].x, expanded[0].y);
  for (let i = 1; i < expanded.length; i++) {
    ctx.lineTo(expanded[i].x, expanded[i].y);
  }
  ctx.closePath();
}

// ─── Component ─────────────────────────────────────────────────────────────

export function AreaSelector({
  imageUrl,
  onMaskGenerated,
  initialMask,
  onPolygonDataChange,
  initialPolygonData,
  compact = false,
  polygonOverride,
}: AreaSelectorProps) {
  // === Canvas and Context Refs ===
  const displayCanvasRef = useRef<HTMLCanvasElement>(null);
  const maskCanvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const displayCtxRef = useRef<CanvasRenderingContext2D | null>(null);
  const maskCtxRef = useRef<CanvasRenderingContext2D | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const maskLoadedRef = useRef(false);

  // === Polygon State ===
  const [polygons, setPolygons] = useState<Polygon[]>([]);
  const [activePoints, setActivePoints] = useState<Point[]>([]);
  const [cursorPos, setCursorPos] = useState<Point | null>(null);
  const [isNearFirstPoint, setIsNearFirstPoint] = useState(false);
  const [isDeleteMode, setIsDeleteMode] = useState(false);
  const [isAddPointMode, setIsAddPointMode] = useState(false);
  const [hoveredPolygonId, setHoveredPolygonId] = useState<string | null>(null);
  const [hoveredEdgeInsert, setHoveredEdgeInsert] = useState<{
    polygonId: string;
    segmentIndex: number;
    point: Point;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // === Shape Mode ===
  const [shapeMode, setShapeMode] = useState<ShapeMode>("straight");

  // === Screen-reader status announcements (aria-live) ===
  const [announcement, setAnnouncement] = useState("");
  const prevPolygonCount = useRef(0);

  // === Snap-to-Edge Toggle (off by default) ===
  const [snapToEdge, setSnapToEdge] = useState(false);

  // === Drag-to-Edit State ===
  const [draggingInfo, setDraggingInfo] = useState<{
    polygonId: string;
    pointIndex: number;
  } | null>(null);
  const isDragging = useRef(false);
  const dragStartPos = useRef<Point | null>(null);

  // === Canvas Initialization ===
  useEffect(() => {
    const displayCanvas = displayCanvasRef.current;
    const maskCanvas = maskCanvasRef.current;
    if (!displayCanvas || !maskCanvas) return;

    const displayCtx = displayCanvas.getContext("2d", {
      willReadFrequently: true,
    });
    const maskCtx = maskCanvas.getContext("2d", { willReadFrequently: true });
    if (!displayCtx || !maskCtx) return;

    displayCtxRef.current = displayCtx;
    maskCtxRef.current = maskCtx;

    loadImage();

    const handleResize = () => {
      if (imageRef.current?.complete) {
        updateCanvasSize();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [imageUrl]);

  // === Redraw whenever polygons, active points, or cursor changes ===
  useEffect(() => {
    updateDisplayCanvas();
    renderMaskCanvas();
    if (polygons.length > 0) {
      exportMask();
    } else if (activePoints.length === 0) {
      onMaskGenerated("");
    }
  }, [
    polygons,
    activePoints,
    cursorPos,
    isNearFirstPoint,
    hoveredPolygonId,
    hoveredEdgeInsert,
    shapeMode,
    draggingInfo,
    snapToEdge,
  ]);

  // === External polygon replacement ===
  useEffect(() => {
    if (!polygonOverride) return;
    const canvas = displayCanvasRef.current;
    if (!canvas) return;
    const cw = canvas.width;
    const ch = canvas.height;
    setActivePoints([]);
    setPolygons(
      polygonOverride.data.polygons.map((p) => ({
        id: p.id,
        points: p.points.map((pt) => ({ x: pt.x * cw, y: pt.y * ch })),
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polygonOverride?.version]);

  // === Image Loading ===
  const loadImage = () => {
    setIsLoading(true);

    const img = new Image();
    // No crossOrigin needed: exportMask() uses maskCanvas (never draws the source image),
    // so the display canvas being tainted by cross-origin images is harmless.
    imageRef.current = img;

    img.onload = () => {
      updateCanvasSize();

      if (!maskLoadedRef.current) {
        maskLoadedRef.current = true;

        // Prefer polygon data (editable) over flat mask image
        if (initialPolygonData && initialPolygonData.polygons.length > 0) {
          const canvas = displayCanvasRef.current;
          if (canvas) {
            const cw = canvas.width;
            const ch = canvas.height;
            const restored: Polygon[] = initialPolygonData.polygons.map(
              (p) => ({
                id: p.id,
                points: p.points.map((pt) => ({ x: pt.x * cw, y: pt.y * ch })),
              })
            );
            setPolygons(restored);
            if (initialPolygonData.shapeMode) {
              setShapeMode(initialPolygonData.shapeMode);
            }
          }
        } else if (initialMask && initialMask.startsWith("data:image")) {
          onMaskGenerated(initialMask);
        }
      }

      setIsLoading(false);
    };

    img.onerror = () => {
      setIsLoading(false);
    };

    img.src = imageUrl;
  };

  // === Canvas Sizing ===
  const updateCanvasSize = () => {
    const displayCanvas = displayCanvasRef.current;
    const maskCanvas = maskCanvasRef.current;
    const container = containerRef.current;
    const image = imageRef.current;

    if (!displayCanvas || !maskCanvas || !container || !image) return;

    const containerRect = container.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;
    const imageAspectRatio = image.width / image.height;

    let canvasWidth: number;
    let canvasHeight: number;

    if (imageAspectRatio > containerWidth / containerHeight) {
      canvasWidth = containerWidth;
      canvasHeight = containerWidth / imageAspectRatio;
    } else {
      canvasHeight = containerHeight;
      canvasWidth = containerHeight * imageAspectRatio;
    }

    canvasWidth *= 0.995;
    canvasHeight *= 0.995;

    displayCanvas.width = canvasWidth;
    displayCanvas.height = canvasHeight;
    maskCanvas.width = canvasWidth;
    maskCanvas.height = canvasHeight;

    updateDisplayCanvas();
  };

  // === Display Canvas: photo + polygons (smooth or straight) + active drawing ===
  const updateDisplayCanvas = () => {
    const displayCanvas = displayCanvasRef.current;
    const displayCtx = displayCtxRef.current;
    const image = imageRef.current;

    if (!displayCanvas || !displayCtx || !image) return;
    if (!image.complete || image.naturalWidth === 0) return;

    const cw = displayCanvas.width;
    const ch = displayCanvas.height;

    // Clear
    displayCtx.clearRect(0, 0, cw, ch);

    // Draw background image
    displayCtx.drawImage(image, 0, 0, cw, ch);

    // Draw completed polygons
    for (const polygon of polygons) {
      if (polygon.points.length < 3) continue;

      const isHovered = isDeleteMode && hoveredPolygonId === polygon.id;
      const isBeingDragged = draggingInfo?.polygonId === polygon.id;

      // When dragging, always show straight edges for precision
      const useSmooth = shapeMode === "rounded" && !isBeingDragged;

      if (useSmooth) {
        traceSmoothedPath(displayCtx, polygon.points, cw, ch, snapToEdge);
      } else {
        traceStraightPath(displayCtx, polygon.points, cw, ch, snapToEdge);
      }

      // Fill
      displayCtx.fillStyle = isHovered ? "rgba(239, 68, 68, 0.4)" : UI_FILL;
      displayCtx.fill();

      // Stroke
      displayCtx.strokeStyle = isHovered ? "rgba(239, 68, 68, 0.9)" : UI_STROKE;
      displayCtx.lineWidth = 2;
      displayCtx.stroke();

      // Draw control point handles on completed polygons (always visible for editing)
      if (!isDeleteMode) {
        for (let i = 0; i < polygon.points.length; i++) {
          const pt = polygon.points[i];
          const isDraggedPoint =
            isBeingDragged && draggingInfo?.pointIndex === i;
          const isEdgeSnapped = snapToEdge && isNearEdge(pt, cw, ch);

          displayCtx.beginPath();
          displayCtx.arc(pt.x, pt.y, isDraggedPoint ? 7 : 5, 0, Math.PI * 2);
          displayCtx.fillStyle = isDraggedPoint
            ? UI_DRAG_POINT
            : isEdgeSnapped
              ? UI_EDGE_SNAP
              : UI_POINT;
          displayCtx.fill();
          displayCtx.strokeStyle = "white";
          displayCtx.lineWidth = 1.5;
          displayCtx.stroke();
        }
      }
    }

    // Draw add-point insertion preview
    if (isAddPointMode && hoveredEdgeInsert) {
      const pt = hoveredEdgeInsert.point;
      // Draw a "+" indicator at the insertion point (green to match UI)
      displayCtx.beginPath();
      displayCtx.arc(pt.x, pt.y, 7, 0, Math.PI * 2);
      displayCtx.fillStyle = "rgba(34, 197, 94, 0.9)";
      displayCtx.fill();
      displayCtx.strokeStyle = "white";
      displayCtx.lineWidth = 2;
      displayCtx.stroke();
      // Draw a small + inside
      displayCtx.beginPath();
      displayCtx.moveTo(pt.x - 3.5, pt.y);
      displayCtx.lineTo(pt.x + 3.5, pt.y);
      displayCtx.moveTo(pt.x, pt.y - 3.5);
      displayCtx.lineTo(pt.x, pt.y + 3.5);
      displayCtx.strokeStyle = "white";
      displayCtx.lineWidth = 1.5;
      displayCtx.stroke();
    }

    // Draw edge snap indicators — highlight canvas edges where points will snap
    if (snapToEdge && !isDeleteMode && activePoints.length > 0) {
      const lastPt = cursorPos || activePoints[activePoints.length - 1];
      if (lastPt) {
        displayCtx.save();
        displayCtx.strokeStyle = UI_EDGE_SNAP;
        displayCtx.lineWidth = 3;
        displayCtx.setLineDash([8, 4]);

        if (lastPt.x <= EDGE_SNAP_THRESHOLD) {
          displayCtx.beginPath();
          displayCtx.moveTo(0, 0);
          displayCtx.lineTo(0, ch);
          displayCtx.stroke();
        }
        if (lastPt.x >= cw - EDGE_SNAP_THRESHOLD) {
          displayCtx.beginPath();
          displayCtx.moveTo(cw, 0);
          displayCtx.lineTo(cw, ch);
          displayCtx.stroke();
        }
        if (lastPt.y <= EDGE_SNAP_THRESHOLD) {
          displayCtx.beginPath();
          displayCtx.moveTo(0, 0);
          displayCtx.lineTo(cw, 0);
          displayCtx.stroke();
        }
        if (lastPt.y >= ch - EDGE_SNAP_THRESHOLD) {
          displayCtx.beginPath();
          displayCtx.moveTo(0, ch);
          displayCtx.lineTo(cw, ch);
          displayCtx.stroke();
        }

        displayCtx.restore();
      }
    }

    // Draw active polygon points and lines (always straight while drawing)
    if (activePoints.length > 0) {
      displayCtx.beginPath();
      displayCtx.moveTo(activePoints[0].x, activePoints[0].y);
      for (let i = 1; i < activePoints.length; i++) {
        displayCtx.lineTo(activePoints[i].x, activePoints[i].y);
      }

      if (cursorPos && activePoints.length > 0) {
        displayCtx.lineTo(cursorPos.x, cursorPos.y);
      }

      displayCtx.strokeStyle = UI_STROKE;
      displayCtx.lineWidth = 2;
      displayCtx.setLineDash([6, 4]);
      displayCtx.stroke();
      displayCtx.setLineDash([]);

      // Draw vertex dots
      for (let i = 0; i < activePoints.length; i++) {
        const pt = activePoints[i];
        const isFirst = i === 0;
        const isEdgeSnapped = snapToEdge && isNearEdge(pt, cw, ch);

        displayCtx.beginPath();
        displayCtx.arc(
          pt.x,
          pt.y,
          isFirst && isNearFirstPoint ? 8 : 5,
          0,
          Math.PI * 2
        );
        displayCtx.fillStyle =
          isFirst && isNearFirstPoint
            ? UI_SNAP
            : isEdgeSnapped
              ? UI_EDGE_SNAP
              : UI_POINT;
        displayCtx.fill();
        displayCtx.strokeStyle = "white";
        displayCtx.lineWidth = 1.5;
        displayCtx.stroke();
      }
    }
  };

  // === Mask Canvas: solid red fills for AI export ===
  const renderMaskCanvas = () => {
    const maskCanvas = maskCanvasRef.current;
    const maskCtx = maskCtxRef.current;
    if (!maskCanvas || !maskCtx) return;

    const cw = maskCanvas.width;
    const ch = maskCanvas.height;

    maskCtx.clearRect(0, 0, cw, ch);

    for (const polygon of polygons) {
      if (polygon.points.length < 3) continue;

      if (shapeMode === "rounded") {
        traceSmoothedPath(maskCtx, polygon.points, cw, ch, snapToEdge);
      } else {
        traceStraightPath(maskCtx, polygon.points, cw, ch, snapToEdge);
      }

      maskCtx.fillStyle = MASK_FILL;
      maskCtx.fill();
    }
  };

  // === Export Mask ===
  const exportMask = () => {
    const maskCanvas = maskCanvasRef.current;
    if (!maskCanvas) return;

    const maskDataUrl = maskCanvas.toDataURL("image/png", 1.0);
    onMaskGenerated(maskDataUrl);
  };

  // === Export polygon data as normalized (0-1) coordinates ===
  const emitPolygonData = useCallback(() => {
    if (!onPolygonDataChange) return;
    const canvas = displayCanvasRef.current;
    if (!canvas || polygons.length === 0) {
      onPolygonDataChange(null);
      return;
    }
    const cw = canvas.width;
    const ch = canvas.height;
    onPolygonDataChange({
      polygons: polygons.map((p) => ({
        id: p.id,
        points: p.points.map((pt) => ({ x: pt.x / cw, y: pt.y / ch })),
      })),
      shapeMode,
    });
  }, [polygons, shapeMode, onPolygonDataChange]);

  // === Emit polygon data whenever polygons or shapeMode change ===
  useEffect(() => {
    emitPolygonData();
  }, [emitPolygonData]);

  // === Announce area count changes to screen readers ===
  useEffect(() => {
    const n = polygons.length;
    const prev = prevPolygonCount.current;
    if (n > prev) {
      setAnnouncement(
        `Design area added. ${n} area${n === 1 ? "" : "s"} selected.`
      );
    } else if (n < prev) {
      setAnnouncement(
        n === 0
          ? "All design areas cleared."
          : `Design area removed. ${n} area${n === 1 ? "" : "s"} selected.`
      );
    }
    prevPolygonCount.current = n;
  }, [polygons.length]);

  // === Coordinate Transformation ===
  const getCoordinates = (
    event: React.MouseEvent | React.TouchEvent
  ): Point | null => {
    const canvas = displayCanvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    if ("touches" in event) {
      const touch = event.touches[0];
      if (!touch) return null;
      return {
        x: (touch.clientX - rect.left) * scaleX,
        y: (touch.clientY - rect.top) * scaleY,
      };
    } else {
      return {
        x: (event.clientX - rect.left) * scaleX,
        y: (event.clientY - rect.top) * scaleY,
      };
    }
  };

  // === Point distance ===
  const distance = (a: Point, b: Point): number =>
    Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);

  // === Hit test: is point inside a polygon? (ray casting) ===
  const isPointInPolygon = (point: Point, poly: Point[]): boolean => {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const xi = poly[i].x,
        yi = poly[i].y;
      const xj = poly[j].x,
        yj = poly[j].y;
      const intersect =
        yi > point.y !== yj > point.y &&
        point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;
      if (intersect) inside = !inside;
    }
    return inside;
  };

  // === Find closest point on a line segment (for add-point tool) ===
  const closestPointOnSegment = (
    p: Point,
    a: Point,
    b: Point
  ): { point: Point; dist: number; t: number } => {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const lenSq = dx * dx + dy * dy;
    if (lenSq === 0) return { point: { ...a }, dist: distance(p, a), t: 0 };
    let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq;
    t = Math.max(0, Math.min(1, t));
    const proj = { x: a.x + t * dx, y: a.y + t * dy };
    return { point: proj, dist: distance(p, proj), t };
  };

  // === Find nearest edge (line segment) on any completed polygon ===
  const findNearestEdge = (
    coords: Point
  ): {
    polygonId: string;
    segmentIndex: number;
    point: Point;
    dist: number;
  } | null => {
    let nearest: {
      polygonId: string;
      segmentIndex: number;
      point: Point;
      dist: number;
    } | null = null;

    const edgeThreshold = 15;

    for (const polygon of polygons) {
      for (let i = 0; i < polygon.points.length; i++) {
        const a = polygon.points[i];
        const b = polygon.points[(i + 1) % polygon.points.length];
        const { point, dist, t } = closestPointOnSegment(coords, a, b);
        // Don't insert too close to existing control points
        if (t < 0.1 || t > 0.9) continue;
        if (dist < edgeThreshold && (!nearest || dist < nearest.dist)) {
          nearest = { polygonId: polygon.id, segmentIndex: i, point, dist };
        }
      }
    }

    return nearest;
  };

  // === Find nearest control point on any completed polygon ===
  const findNearestControlPoint = (
    coords: Point
  ): { polygonId: string; pointIndex: number; dist: number } | null => {
    let nearest: {
      polygonId: string;
      pointIndex: number;
      dist: number;
    } | null = null;

    for (const polygon of polygons) {
      for (let i = 0; i < polygon.points.length; i++) {
        const d = distance(coords, polygon.points[i]);
        if (d < POINT_GRAB_THRESHOLD && (!nearest || d < nearest.dist)) {
          nearest = { polygonId: polygon.id, pointIndex: i, dist: d };
        }
      }
    }

    return nearest;
  };

  // === Mouse Down: start drag if near a control point ===
  const handleMouseDown = (event: React.MouseEvent) => {
    if (isDeleteMode || activePoints.length > 0) return;

    const coords = getCoordinates(event);
    if (!coords) return;

    const nearest = findNearestControlPoint(coords);
    if (nearest) {
      event.preventDefault();
      isDragging.current = true;
      dragStartPos.current = coords;
      setDraggingInfo({
        polygonId: nearest.polygonId,
        pointIndex: nearest.pointIndex,
      });
    }
  };

  // === Mouse Move: drag control point or update cursor ===
  const handleMouseMove = (event: React.MouseEvent) => {
    const coords = getCoordinates(event);
    if (!coords) return;

    // Dragging a control point
    if (isDragging.current && draggingInfo) {
      event.preventDefault();
      setPolygons((prev) =>
        prev.map((polygon) => {
          if (polygon.id !== draggingInfo.polygonId) return polygon;
          const newPoints = [...polygon.points];
          newPoints[draggingInfo.pointIndex] = coords;
          return { ...polygon, points: newPoints };
        })
      );
      return;
    }

    setCursorPos(coords);

    // Check snap to first point
    if (activePoints.length >= 3) {
      setIsNearFirstPoint(distance(coords, activePoints[0]) < CLOSE_THRESHOLD);
    } else {
      setIsNearFirstPoint(false);
    }

    // Check hover over polygons in delete mode
    if (isDeleteMode) {
      let found: string | null = null;
      for (let i = polygons.length - 1; i >= 0; i--) {
        if (isPointInPolygon(coords, polygons[i].points)) {
          found = polygons[i].id;
          break;
        }
      }
      setHoveredPolygonId(found);
    }

    // Check hover over edges in add-point mode
    if (isAddPointMode) {
      const nearest = findNearestEdge(coords);
      setHoveredEdgeInsert(
        nearest
          ? {
              polygonId: nearest.polygonId,
              segmentIndex: nearest.segmentIndex,
              point: nearest.point,
            }
          : null
      );
    }
  };

  // === Mouse Up: finish drag ===
  const handleMouseUp = () => {
    if (isDragging.current) {
      isDragging.current = false;
      dragStartPos.current = null;
      setDraggingInfo(null);
    }
  };

  // === Click Handler ===
  const handleCanvasClick = (event: React.MouseEvent) => {
    // If we just finished a drag, don't process as click
    if (draggingInfo) return;

    event.preventDefault();
    const coords = getCoordinates(event);
    if (!coords) return;

    // Delete mode: check if clicking inside a completed polygon
    if (isDeleteMode) {
      for (let i = polygons.length - 1; i >= 0; i--) {
        if (isPointInPolygon(coords, polygons[i].points)) {
          setPolygons((prev) => prev.filter((_, idx) => idx !== i));
          return;
        }
      }
      return;
    }

    // Add-point mode: insert a new control point on the nearest edge
    // But skip if near an existing control point (user can drag those instead)
    if (isAddPointMode) {
      const nearControl = findNearestControlPoint(coords);
      if (nearControl) return; // Don't insert — user is near an existing point
      const nearest = findNearestEdge(coords);
      if (nearest) {
        setPolygons((prev) =>
          prev.map((polygon) => {
            if (polygon.id !== nearest.polygonId) return polygon;
            const newPoints = [...polygon.points];
            // Insert after the segment start index
            newPoints.splice(nearest.segmentIndex + 1, 0, nearest.point);
            return { ...polygon, points: newPoints };
          })
        );
        setHoveredEdgeInsert(null);
      }
      return;
    }

    // If clicking near a control point of a completed polygon (but not dragging),
    // don't add a new point — user is trying to edit
    if (activePoints.length === 0) {
      const nearest = findNearestControlPoint(coords);
      if (nearest) return;
    }

    // Drawing mode: close polygon
    if (activePoints.length >= 3) {
      if (distance(coords, activePoints[0]) < CLOSE_THRESHOLD) {
        const newPolygon: Polygon = {
          id: crypto.randomUUID(),
          points: [...activePoints],
        };
        setPolygons((prev) => [...prev, newPolygon]);
        setActivePoints([]);
        setIsNearFirstPoint(false);
        setCursorPos(null);
        return;
      }
    }

    // Add a new point
    setActivePoints((prev) => [...prev, coords]);
  };

  const handleMouseLeave = () => {
    setCursorPos(null);
    setIsNearFirstPoint(false);
    setHoveredPolygonId(null);
    setHoveredEdgeInsert(null);
    if (isDragging.current) {
      isDragging.current = false;
      dragStartPos.current = null;
      setDraggingInfo(null);
    }
  };

  // === Touch handlers ===
  const handleTouchStart = (event: React.TouchEvent) => {
    if (isDeleteMode || activePoints.length > 0) return;

    const coords = getCoordinates(event);
    if (!coords) return;

    const nearest = findNearestControlPoint(coords);
    if (nearest) {
      event.preventDefault();
      isDragging.current = true;
      dragStartPos.current = coords;
      setDraggingInfo({
        polygonId: nearest.polygonId,
        pointIndex: nearest.pointIndex,
      });
    }
  };

  const handleTouchMove = (event: React.TouchEvent) => {
    if (!isDragging.current || !draggingInfo) return;

    event.preventDefault();
    const coords = getCoordinates(event);
    if (!coords) return;

    setPolygons((prev) =>
      prev.map((polygon) => {
        if (polygon.id !== draggingInfo.polygonId) return polygon;
        const newPoints = [...polygon.points];
        newPoints[draggingInfo.pointIndex] = coords;
        return { ...polygon, points: newPoints };
      })
    );
  };

  const handleTouchEnd = (event: React.TouchEvent) => {
    // If was dragging, just end the drag
    if (isDragging.current) {
      isDragging.current = false;
      dragStartPos.current = null;
      setDraggingInfo(null);
      return;
    }

    event.preventDefault();
    const touch = event.changedTouches[0];
    if (!touch) return;

    const canvas = displayCanvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const coords: Point = {
      x: (touch.clientX - rect.left) * scaleX,
      y: (touch.clientY - rect.top) * scaleY,
    };

    if (isDeleteMode) {
      for (let i = polygons.length - 1; i >= 0; i--) {
        if (isPointInPolygon(coords, polygons[i].points)) {
          setPolygons((prev) => prev.filter((_, idx) => idx !== i));
          return;
        }
      }
      return;
    }

    // Add-point mode on touch
    if (isAddPointMode) {
      const nearControl = findNearestControlPoint(coords);
      if (nearControl) return; // Don't insert — user is near an existing point
      const nearest = findNearestEdge(coords);
      if (nearest) {
        setPolygons((prev) =>
          prev.map((polygon) => {
            if (polygon.id !== nearest.polygonId) return polygon;
            const newPoints = [...polygon.points];
            newPoints.splice(nearest.segmentIndex + 1, 0, nearest.point);
            return { ...polygon, points: newPoints };
          })
        );
      }
      return;
    }

    // Don't add point if near a control point
    if (activePoints.length === 0) {
      const nearest = findNearestControlPoint(coords);
      if (nearest) return;
    }

    // Close polygon if near first point
    if (activePoints.length >= 3) {
      if (distance(coords, activePoints[0]) < CLOSE_THRESHOLD * 1.5) {
        const newPolygon: Polygon = {
          id: crypto.randomUUID(),
          points: [...activePoints],
        };
        setPolygons((prev) => [...prev, newPolygon]);
        setActivePoints([]);
        return;
      }
    }

    setActivePoints((prev) => [...prev, coords]);
  };

  // === Undo ===
  const undo = () => {
    if (activePoints.length > 0) {
      // Remove last point while drawing
      setActivePoints((prev) => prev.slice(0, -1));
    } else if (polygons.length > 0) {
      // Re-open last polygon as active points so user can undo point-by-point
      const lastPolygon = polygons[polygons.length - 1];
      setPolygons((prev) => prev.slice(0, -1));
      setActivePoints(lastPolygon.points);
    }
  };

  // === Clear All ===
  const clearAll = () => {
    setPolygons([]);
    setActivePoints([]);
    setCursorPos(null);
    setIsNearFirstPoint(false);
    setDraggingInfo(null);
    isDragging.current = false;
  };

  // === Escape key: cancel current polygon ===
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setActivePoints([]);
        setCursorPos(null);
        setIsNearFirstPoint(false);
        if (isDragging.current) {
          isDragging.current = false;
          setDraggingInfo(null);
        }
      }
      if ((e.metaKey || e.ctrlKey) && e.key === "z") {
        e.preventDefault();
        undo();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activePoints, polygons]);

  // Determine cursor style
  const getCursorClass = (): string => {
    if (isDeleteMode) return "cursor-pointer";
    if (isAddPointMode) {
      if (cursorPos) {
        const nearControl = findNearestControlPoint(cursorPos);
        if (nearControl) return "cursor-grab";
      }
      return hoveredEdgeInsert ? "cursor-copy" : "cursor-crosshair";
    }
    if (isNearFirstPoint) return "cursor-pointer";
    if (draggingInfo) return "cursor-grabbing";
    // Check if hovering near a control point
    if (cursorPos && activePoints.length === 0) {
      const nearest = findNearestControlPoint(cursorPos);
      if (nearest) return "cursor-grab";
    }
    return "cursor-crosshair";
  };

  const hasContent = polygons.length > 0 || activePoints.length > 0;
  const canUndo = activePoints.length > 0 || polygons.length > 0;

  // Shared visible focus style for keyboard users (a11y — PLA2-374)
  const focusRing =
    "focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-plantista-500 dark:focus-visible:ring-plantista-400 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-plantista-900";

  // Shared toolbar buttons
  const toolbarButtons = (
    <>
      {/* Drawing Tools */}
      <div className={`flex ${compact ? "" : "md:flex-col"} gap-1.5`}>
        <button
          onClick={() => {
            setIsDeleteMode(false);
            setIsAddPointMode(false);
            setHoveredEdgeInsert(null);
          }}
          className={`${compact ? "p-1.5" : "p-2.5 md:p-3"} rounded-lg transition flex items-center justify-center ${focusRing} ${
            !isDeleteMode && !isAddPointMode
              ? "bg-plantista-500 dark:bg-plantista-400 text-white dark:text-plantista-950 shadow-md"
              : compact
                ? "bg-white/80 dark:bg-plantista-800/80 text-gray-600 dark:text-plantista-300 hover:bg-white dark:hover:bg-plantista-700"
                : "bg-gray-100 dark:bg-plantista-800 text-gray-600 dark:text-plantista-300 hover:bg-gray-200 dark:hover:bg-plantista-700"
          }`}
          aria-label="Draw polygon tool: click to place points outlining the planting area"
          aria-pressed={!isDeleteMode && !isAddPointMode}
          title="Draw Polygon"
        >
          <Pentagon
            className={compact ? "w-4 h-4" : "w-5 h-5"}
            aria-hidden="true"
          />
        </button>
        <button
          onClick={() => {
            if (isAddPointMode) {
              setIsAddPointMode(false);
              setHoveredEdgeInsert(null);
            } else {
              setIsAddPointMode(true);
              setIsDeleteMode(false);
              setActivePoints([]);
              setCursorPos(null);
            }
          }}
          className={`${compact ? "p-1.5" : "p-2.5 md:p-3"} rounded-lg transition flex items-center justify-center ${focusRing} ${
            isAddPointMode
              ? "bg-plantista-500 dark:bg-plantista-400 text-white dark:text-plantista-950 shadow-md"
              : compact
                ? "bg-white/80 dark:bg-plantista-800/80 text-gray-600 dark:text-plantista-300 hover:bg-white dark:hover:bg-plantista-700"
                : "bg-gray-100 dark:bg-plantista-800 text-gray-600 dark:text-plantista-300 hover:bg-gray-200 dark:hover:bg-plantista-700"
          }`}
          aria-label="Add point tool: click on an edge between two points to insert a new point"
          aria-pressed={isAddPointMode}
          title="Add Point (click on an edge between two points)"
        >
          <Plus
            className={compact ? "w-4 h-4" : "w-5 h-5"}
            aria-hidden="true"
          />
        </button>
        <button
          onClick={() => {
            setIsDeleteMode(true);
            setIsAddPointMode(false);
            setHoveredEdgeInsert(null);
            setActivePoints([]);
            setCursorPos(null);
          }}
          className={`${compact ? "p-1.5" : "p-2.5 md:p-3"} rounded-lg transition flex items-center justify-center ${focusRing} ${
            isDeleteMode
              ? "bg-red-500 dark:bg-red-400 text-white dark:text-red-950 shadow-md"
              : compact
                ? "bg-white/80 dark:bg-plantista-800/80 text-gray-600 dark:text-plantista-300 hover:bg-white dark:hover:bg-plantista-700"
                : "bg-gray-100 dark:bg-plantista-800 text-gray-600 dark:text-plantista-300 hover:bg-gray-200 dark:hover:bg-plantista-700"
          }`}
          aria-label="Delete polygon tool: click a completed area to remove it"
          aria-pressed={isDeleteMode}
          title="Delete Polygon"
        >
          <Trash2
            className={compact ? "w-4 h-4" : "w-5 h-5"}
            aria-hidden="true"
          />
        </button>
      </div>

      {/* Divider */}
      <div
        className={
          compact
            ? "w-px h-5 bg-gray-300/50 dark:bg-plantista-600/50"
            : "w-px h-6 md:w-full md:h-px bg-gray-200 dark:bg-plantista-800"
        }
      />

      {/* Shape Mode Toggle */}
      <div className={`flex ${compact ? "" : "md:flex-col"} gap-1.5`}>
        <button
          onClick={() => setShapeMode("rounded")}
          className={`${compact ? "p-1.5" : "p-2.5 md:p-3"} rounded-lg transition flex items-center justify-center ${focusRing} ${
            shapeMode === "rounded"
              ? compact
                ? "bg-plantista-100/90 dark:bg-plantista-800/90 text-plantista-700 dark:text-plantista-300 ring-2 ring-plantista-500 dark:ring-plantista-400"
                : "bg-plantista-100 dark:bg-plantista-800 text-plantista-700 dark:text-plantista-300 ring-2 ring-plantista-500 dark:ring-plantista-400"
              : compact
                ? "bg-white/80 dark:bg-plantista-800/80 text-gray-600 dark:text-plantista-300 hover:bg-white dark:hover:bg-plantista-700"
                : "bg-gray-100 dark:bg-plantista-800 text-gray-600 dark:text-plantista-300 hover:bg-gray-200 dark:hover:bg-plantista-700"
          }`}
          aria-label="Rounded edges: draw areas with smooth curves"
          aria-pressed={shapeMode === "rounded"}
          title="Rounded Edges (smooth curves)"
        >
          <Spline
            className={compact ? "w-4 h-4" : "w-5 h-5"}
            aria-hidden="true"
          />
        </button>
        <button
          onClick={() => setShapeMode("straight")}
          className={`${compact ? "p-1.5" : "p-2.5 md:p-3"} rounded-lg transition flex items-center justify-center ${focusRing} ${
            shapeMode === "straight"
              ? compact
                ? "bg-plantista-100/90 dark:bg-plantista-800/90 text-plantista-700 dark:text-plantista-300 ring-2 ring-plantista-500 dark:ring-plantista-400"
                : "bg-plantista-100 dark:bg-plantista-800 text-plantista-700 dark:text-plantista-300 ring-2 ring-plantista-500 dark:ring-plantista-400"
              : compact
                ? "bg-white/80 dark:bg-plantista-800/80 text-gray-600 dark:text-plantista-300 hover:bg-white dark:hover:bg-plantista-700"
                : "bg-gray-100 dark:bg-plantista-800 text-gray-600 dark:text-plantista-300 hover:bg-gray-200 dark:hover:bg-plantista-700"
          }`}
          aria-label="Straight edges: draw areas with straight lines"
          aria-pressed={shapeMode === "straight"}
          title="Straight Edges"
        >
          <svg
            className={compact ? "w-4 h-4" : "w-5 h-5"}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <line x1="6" y1="12" x2="18" y2="12" />
            <circle cx="5" cy="12" r="2" />
            <circle cx="19" cy="12" r="2" />
          </svg>
        </button>
      </div>

      {/* Divider */}
      <div
        className={
          compact
            ? "w-px h-5 bg-gray-300/50 dark:bg-plantista-600/50"
            : "w-px h-6 md:w-full md:h-px bg-gray-200 dark:bg-plantista-800"
        }
      />

      {/* Snap-to-Edge Toggle */}
      <div className={`flex ${compact ? "" : "md:flex-col"} gap-1.5`}>
        <button
          onClick={() => setSnapToEdge((v) => !v)}
          className={`${compact ? "p-1.5" : "p-2.5 md:p-3"} rounded-lg transition flex items-center justify-center ${focusRing} ${
            snapToEdge
              ? compact
                ? "bg-plantista-100/90 dark:bg-plantista-800/90 text-plantista-700 dark:text-plantista-300 ring-2 ring-plantista-500 dark:ring-plantista-400"
                : "bg-plantista-100 dark:bg-plantista-800 text-plantista-700 dark:text-plantista-300 ring-2 ring-plantista-500 dark:ring-plantista-400"
              : compact
                ? "bg-white/80 dark:bg-plantista-800/80 text-gray-600 dark:text-plantista-300 hover:bg-white dark:hover:bg-plantista-700"
                : "bg-gray-100 dark:bg-plantista-800 text-gray-600 dark:text-plantista-300 hover:bg-gray-200 dark:hover:bg-plantista-700"
          }`}
          aria-label={
            snapToEdge
              ? "Snap to edge: on. Points near image edges snap to the boundary. Activate to turn off."
              : "Snap to edge: off. Activate to snap points near image edges to the boundary."
          }
          aria-pressed={snapToEdge}
          title={
            snapToEdge
              ? "Snap to Edge: On (points near image edges snap to the boundary)"
              : "Snap to Edge: Off"
          }
        >
          <Magnet
            className={compact ? "w-4 h-4" : "w-5 h-5"}
            aria-hidden="true"
          />
        </button>
      </div>

      {/* Divider */}
      <div
        className={
          compact
            ? "w-px h-5 bg-gray-300/50 dark:bg-plantista-600/50"
            : "w-px h-6 md:w-full md:h-px bg-gray-200 dark:bg-plantista-800"
        }
      />

      {/* Actions */}
      <div className={`flex ${compact ? "" : "md:flex-col"} gap-1.5`}>
        <button
          onClick={undo}
          disabled={!canUndo}
          className={`${compact ? "p-1.5 bg-white/80 dark:bg-plantista-800/80 hover:bg-white dark:hover:bg-plantista-700" : "p-2.5 md:p-3 bg-gray-100 dark:bg-plantista-800 hover:bg-gray-200 dark:hover:bg-plantista-700"} rounded-lg text-gray-600 dark:text-plantista-300 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center ${focusRing}`}
          aria-label="Undo last point"
          title="Undo (Ctrl+Z)"
        >
          <Undo
            className={compact ? "w-4 h-4" : "w-5 h-5"}
            aria-hidden="true"
          />
        </button>
        <button
          onClick={clearAll}
          disabled={!hasContent}
          className={`${compact ? "p-1.5 bg-white/80 dark:bg-plantista-800/80 hover:bg-white dark:hover:bg-plantista-700" : "p-2.5 md:p-3 bg-gray-100 dark:bg-plantista-800 hover:bg-gray-200 dark:hover:bg-plantista-700"} rounded-lg text-gray-600 dark:text-plantista-300 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center ${focusRing}`}
          aria-label="Clear all design areas"
          title="Clear All"
        >
          <RotateCcw
            className={compact ? "w-4 h-4" : "w-5 h-5"}
            aria-hidden="true"
          />
        </button>
      </div>
    </>
  );

  // Shared canvas element
  const canvasElement = (
    <>
      {isLoading && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white dark:bg-plantista-950">
          <div className="mb-4 h-10 w-10 animate-spin rounded-full border-4 border-gray-200 dark:border-plantista-800 border-t-plantista-500 dark:border-t-plantista-400"></div>
          <p className="text-plantista-600 dark:text-plantista-300">
            Loading image...
          </p>
        </div>
      )}

      {/* Screen-reader-only instructions for the drawing canvas */}
      <p id="area-selector-instructions" className="sr-only">
        Interactive drawing canvas. Use the drawing tools toolbar to select,
        add, delete, or reshape planting areas. On a touch device, tap to place
        points around the area you want to design and tap the first point to
        close the shape. Use the undo and clear buttons in the toolbar to
        correct mistakes. Press Escape to cancel the current shape.
      </p>

      <canvas
        ref={displayCanvasRef}
        role="application"
        aria-label="Planting-area drawing canvas"
        aria-describedby="area-selector-instructions"
        className={`max-h-full max-w-full select-none ${compact ? "" : "rounded-lg"} ${getCursorClass()}`}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onClick={handleCanvasClick}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ touchAction: "none" }}
      />

      {/* Polite live region announcing area selection status to screen readers */}
      <div aria-live="polite" role="status" className="sr-only">
        {announcement}
      </div>

      <canvas ref={maskCanvasRef} className="hidden" aria-hidden="true" />
    </>
  );

  // ─── Compact Mode (for Pro Editor) ──────────────────────────────────────
  if (compact) {
    return (
      <div className="absolute inset-0 flex flex-col">
        {/* Floating toolbar on top */}
        <div
          role="toolbar"
          aria-label="Design area drawing tools"
          className="absolute top-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5 bg-white/90 dark:bg-plantista-900/90 backdrop-blur-sm border border-gray-200/80 dark:border-plantista-700/80 rounded-lg px-2 py-1.5 shadow-lg"
        >
          {toolbarButtons}
        </div>

        {/* Canvas fills remaining space */}
        <div
          ref={containerRef}
          className="flex-1 relative overflow-hidden bg-plantista-100 dark:bg-plantista-950 flex items-center justify-center"
        >
          {canvasElement}
        </div>
      </div>
    );
  }

  // ─── Default Mode (normal design flow) ──────────────────────────────────
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-bold text-plantista-950 dark:text-white">
          Draw Design Area
        </h2>
        <p className="text-plantista-600 dark:text-plantista-300">
          Click to place points and outline where you want plants. Click the
          first point to close the shape.
        </p>
      </div>

      <div className="flex flex-col md:flex-row gap-4">
        {/* Toolbar */}
        <div className="w-full md:w-auto flex-shrink-0">
          <div className="bg-white dark:bg-plantista-900 border-2 border-gray-200 dark:border-plantista-800 rounded-lg p-2 md:p-3 shadow-lg md:sticky md:top-4">
            <div
              role="toolbar"
              aria-label="Design area drawing tools"
              className="flex md:flex-col items-center gap-2 md:gap-3"
            >
              {toolbarButtons}
            </div>
          </div>
        </div>

        {/* Canvas Container */}
        <div
          ref={containerRef}
          className="flex-1 relative border-2 border-gray-200 dark:border-plantista-800 rounded-lg overflow-hidden bg-white dark:bg-plantista-950 aspect-video md:aspect-[16/9] flex items-center justify-center"
        >
          {canvasElement}
        </div>
      </div>

      {/* Mode indicator */}
      <div className="flex items-center gap-3">
        <span
          className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-medium ${
            shapeMode === "rounded"
              ? "bg-plantista-100 dark:bg-plantista-800 text-plantista-700 dark:text-plantista-300"
              : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300"
          }`}
        >
          {shapeMode === "rounded" ? (
            <>
              <Spline className="w-3.5 h-3.5" />
              Rounded edges
            </>
          ) : (
            <>
              <svg
                className="w-3.5 h-3.5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <line x1="6" y1="12" x2="18" y2="12" />
                <circle cx="5" cy="12" r="2" />
                <circle cx="19" cy="12" r="2" />
              </svg>
              Straight edges
            </>
          )}
        </span>
        <span className="text-xs text-plantista-500 dark:text-plantista-400">
          {snapToEdge
            ? "Snap to edge: on — points near edges snap to the image boundary"
            : "Snap to edge: off — toggle the magnet in the toolbar to enable"}
        </span>
      </div>

      <div className="bg-gradient-to-br from-gray-50 to-white dark:from-plantista-900 dark:to-plantista-950 border border-gray-200 dark:border-plantista-800 rounded-xl p-4 md:p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-plantista-500 dark:bg-plantista-400 text-white dark:text-plantista-950 shadow-sm">
            <Pentagon className="w-4 h-4" aria-hidden="true" />
          </div>
          <h3 className="font-bold text-plantista-900 dark:text-plantista-100">
            How to select your planting area
          </h3>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
          {[
            {
              icon: MousePointerClick,
              title: "Place points",
              body: "Click around the area you want to design.",
              tone: "plantista",
            },
            {
              icon: CheckCircle2,
              title: "Close the shape",
              body: "Click the first green point to finish.",
              tone: "plantista",
            },
            {
              icon: Move,
              title: "Reshape",
              body: "Drag any control point to adjust.",
              tone: "blue",
            },
            {
              icon: Plus,
              title: "Add a point",
              body: "Use the + tool to insert between points.",
              tone: "plantista",
            },
            {
              icon: Spline,
              title: "Rounded or straight",
              body: "Toggle edge style in the toolbar.",
              tone: "neutral",
            },
            {
              icon: Magnet,
              title: "Snap to edge",
              body: "Enable the magnet to snap near borders.",
              tone: "neutral",
            },
            {
              icon: Layers,
              title: "Multiple areas",
              body: "Create as many separate zones as you need.",
              tone: "neutral",
            },
            {
              icon: Trash2,
              title: "Delete or cancel",
              body: "Use delete to remove, Esc to cancel.",
              tone: "red",
            },
            {
              icon: Keyboard,
              title: "Undo",
              body: "Ctrl+Z (Cmd+Z) steps back point-by-point.",
              tone: "neutral",
            },
          ].map(({ icon: Icon, title, body, tone }) => {
            const toneClasses = {
              plantista:
                "bg-plantista-100 dark:bg-plantista-800/60 text-plantista-700 dark:text-plantista-300",
              blue: "bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300",
              red: "bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-300",
              neutral:
                "bg-gray-100 dark:bg-plantista-800/60 text-gray-700 dark:text-plantista-300",
            }[tone];

            return (
              <div
                key={title}
                className="flex items-start gap-2.5 rounded-lg bg-white dark:bg-plantista-900 border border-gray-200 dark:border-plantista-800 p-2.5 transition hover:border-plantista-300 dark:hover:border-plantista-600 hover:shadow-sm"
              >
                <div
                  className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md ${toneClasses}`}
                >
                  <Icon className="w-4 h-4" aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-plantista-900 dark:text-plantista-100 leading-tight">
                    {title}
                  </p>
                  <p className="mt-0.5 text-xs text-plantista-600 dark:text-plantista-400 leading-snug">
                    {body}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
